import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  AGENCY_CATEGORIES,
  CATEGORY_LIST,
  CATEGORY_LABELS,
  normalizeCategory,
} from "../constants/agencyCategories";
import {
  buildUpdateUserPayload,
  fetchUserById,
  updateUser,
} from "../services/userService";
import { formatUserAddress } from "../services/authService";

const EditUserPage = () => {
  const { id: routeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = location.state?.activeTab || "Customers";
  const stateUser = location.state?.user;

  const [activeTab] = useState(initialTab);
  const [loading, setLoading] = useState(!stateUser);
  const [saving, setSaving] = useState(false);

  const [id, setId] = useState(stateUser?.id || routeId || "");
  const [name, setName] = useState(stateUser?.name || "");
  const [email, setEmail] = useState(stateUser?.email || "");
  const [phone, setPhone] = useState(stateUser?.phone || "");
  const [address, setAddress] = useState(stateUser?.address || "");
  const [agencyCategory, setAgencyCategory] = useState(AGENCY_CATEGORIES.CARS);

  const isVendor = activeTab === "Vendors";

  useEffect(() => {
    const loadUser = async () => {
      if (!routeId) return;
      setLoading(true);
      try {
        const user = await fetchUserById(routeId);
        setId(user._id || user.id);
        setName(user.name || "");
        setEmail(user.email || "");
        setPhone(user.phone || user.phoneNumber || "");
        setAddress(formatUserAddress(user.address));
        if (user.agencyCategory) {
          setAgencyCategory(normalizeCategory(user.agencyCategory));
        }
      } catch (err) {
        toast.error(err?.friendlyMessage || "Failed to load user");
      } finally {
        setLoading(false);
      }
    };

    if (stateUser?.id) {
      setId(stateUser.id);
      return;
    }
    loadUser();
  }, [routeId, stateUser]);

  const handleUpdate = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const payload = buildUpdateUserPayload({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        addressText: address,
        agencyCategory: isVendor ? agencyCategory : undefined,
      });

      await toast.promise(updateUser(id, payload), {
        loading: "Updating user...",
        success: "User updated successfully",
        error: (e) => e?.friendlyMessage || "Failed to update user",
      });

      navigate(`/Users?selected=${isVendor ? "vendors" : "customers"}`);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="md:p-10 p-4 text-agarly-gray">Loading user...</div>
    );
  }

  return (
    <div className="md:p-10 p-4 pb-4 text-base gap-4 w-full">
      <h1 className="text-2xl font-bold mb-2 dark:text-gray-200">
        Edit {isVendor ? "Vendor" : "Customer"}
      </h1>
      <p className="text-agarly-gray pb-6 text-sm flex flex-wrap gap-1 items-center">
        <Link to="/dashboard" className="hover:underline">Dashboard</Link> /{" "}
        <Link to="/Users" className="hover:underline">Users</Link> /{" "}
        <Link to={`/Users?selected=${isVendor ? "vendors" : "customers"}`} className="hover:underline">
          {activeTab}
        </Link>{" "}
        / <span className="agarly-gradient-text font-bold">Edit</span>
      </p>

      <div className="agarly-card p-6 md:w-[60%] space-y-4">
        <div className="space-y-2">
          <label className="font-bold text-md dark:text-gray-300">Full Name</label>
          <input className="agarly-input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="flex gap-4 md:flex-row flex-col">
          <div className="w-full md:w-1/2 space-y-2">
            <label className="font-bold text-md dark:text-gray-300">Email</label>
            <input
              type="email"
              className="agarly-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="w-full md:w-1/2 space-y-2">
            <label className="font-bold text-md dark:text-gray-300">Phone</label>
            <input className="agarly-input" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
        </div>

        {isVendor && (
          <div className="space-y-2">
            <label className="font-bold text-md dark:text-gray-300">Agency Category</label>
            <select
              className="agarly-input"
              value={agencyCategory}
              onChange={(e) => setAgencyCategory(e.target.value)}
            >
              {CATEGORY_LIST.map((cat) => (
                <option key={cat} value={cat}>
                  {CATEGORY_LABELS[cat]}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="space-y-2">
          <label className="font-bold text-md dark:text-gray-300">Address / City</label>
          <textarea
            className="agarly-input min-h-[100px]"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>

        <button
          type="button"
          disabled={saving}
          onClick={handleUpdate}
          className="agarly-btn-primary w-full py-2 disabled:opacity-50"
        >
          {saving ? "Updating..." : "Update User"}
        </button>
      </div>
      <div style={{ height: "40px" }} />
    </div>
  );
};

export default EditUserPage;
